import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Drawer, Button, Dropdown, Menu, Space, Typography } from "antd";
import { MenuOutlined, DownOutlined, CheckOutlined } from "@ant-design/icons";
import Logo from "../../assests/images/logo-color.png";
import { setUserData } from "../../store/slices/userSlice";

const { Text } = Typography;

const ClientLayout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state?.userData);
  const userType = currentUser?.school_data?.user_type;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only run if we have both current user data and multiple schools
    if (currentUser?.id && currentUser?.school_data_list?.length > 1) {
      const storedSchoolId = localStorage.getItem("selectedSchoolId");

      // If a school ID is stored and it's different from the current one
      if (
        storedSchoolId &&
        storedSchoolId !== currentUser.school_data?.school_id
      ) {
        // Find the stored school in the school_data_list
        const storedSchool = currentUser.school_data_list.find(
          (school) => school.school_id === storedSchoolId
        );

        // If the stored school exists in the list, update the user data
        if (storedSchool) {
          dispatch(
            setUserData({
              ...currentUser,
              school_data: storedSchool,
            })
          );
        }
      }
    }
  }, [currentUser?.id, currentUser?.school_data_list]);

  const handleLogout = () => {
    dispatch({ type: "USER_LOGOUT" });
  };

  const isActive = (path) => {
    return location.pathname === path ? "th-active-menu" : "";
  };

  const showDrawer = () => {
    setVisible(true);
  };

  const onClose = () => {
    setVisible(false);
  };

  const handleSchoolChange = (schoolData) => {
    if (schoolData.school_id !== currentUser?.school_data?.school_id) {
      localStorage.setItem("selectedSchoolId", schoolData.school_id);
      dispatch(
        setUserData({
          ...currentUser,
          school_data: schoolData,
        })
      );

      window.location.reload();
    }
  };

  console.log({ currentUser });
  const schoolMenu = (
    <Menu selectedKeys={[currentUser?.school_data?.school_id]}>
      {currentUser?.school_data_list?.map((school) => {
        const isSelected =
          currentUser?.school_data?.school_id === school.school_id;

        return (
          <Menu.Item
            key={school.school_id}
            onClick={() => handleSchoolChange(school)}
            style={{
              backgroundColor: isSelected ? "#f0f0f0" : "transparent",
              fontWeight: isSelected ? "bold" : "normal",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>
                {school.school_name} ({school.user_type})
              </span>
              {isSelected && <CheckOutlined style={{ color: "#1890ff" }} />}
            </div>
          </Menu.Item>
        );
      })}
    </Menu>
  );

  // School dropdown component
  const SchoolDropdown = () => {
    if (
      !currentUser?.school_data_list ||
      currentUser.school_data_list.length <= 1
    ) {
      return null;
    }

    return (
      <Dropdown overlay={schoolMenu} trigger={["click"]}>
        <a
          className="th-nav-link py-1 px-2 px-md-3 mb-3 mb-md-0"
          onClick={(e) => e.preventDefault()}
          style={{ fontWeight: 600 }}
        >
          <Space>
            {currentUser?.school_data?.school_name}
            <DownOutlined style={{ fontSize: 10 }} />
          </Space>
        </a>
      </Dropdown>
    );
  };

  const isStaff = ["teacher", "coordinator", "admin", "superadmin", "hod", "principal", "academic_director"].includes(userType);
  const isStudent = userType === "student";
  const isParent = userType === "parent";

  const studentAssessmentMenu = (
    <Menu>
      {isStaff && (
        <>
          <Menu.Item key="sa-question-bank" onClick={() => { onClose(); navigate("/question-bank"); }}>
            Question Bank
          </Menu.Item>
          <Menu.Item key="sa-q-bulk-upload" onClick={() => { onClose(); navigate("/question-bank/upload"); }}>
            Question Bulk Upload
          </Menu.Item>
          <Menu.Item key="sa-cycles" onClick={() => { onClose(); navigate("/cycles"); }}>
            Assessment Cycles
          </Menu.Item>
          <Menu.Item key="sa-blueprints" onClick={() => { onClose(); navigate("/blueprints"); }}>
            Blueprints
          </Menu.Item>
          <Menu.Item key="sa-test-papers" onClick={() => { onClose(); navigate("/test-papers"); }}>
            Test Papers
          </Menu.Item>
          <Menu.Item key="sa-performance" onClick={() => { onClose(); navigate("/performance"); }}>
            Student Performance
          </Menu.Item>
          <Menu.Item key="sa-evaluate" onClick={() => { onClose(); navigate("/evaluate-responses"); }}>
            Evaluate SA/LA
          </Menu.Item>
          <Menu.Item key="sa-mgmt-dashboard" onClick={() => { onClose(); navigate("/management-dashboard"); }}>
            Management Dashboard
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item key="sa-sections" onClick={() => { onClose(); navigate("/sections"); }}>
            Sections
          </Menu.Item>
          <Menu.Item key="sa-students" onClick={() => { onClose(); navigate("/students"); }}>
            Students
          </Menu.Item>
          <Menu.Item key="sa-student-bulk-upload" onClick={() => { onClose(); navigate("/students/upload"); }}>
            Student Bulk Upload
          </Menu.Item>
        </>
      )}
      {isStudent && (
        <>
          <Menu.Item key="sa-my-tests" onClick={() => { onClose(); navigate("/my-tests"); }}>
            My Tests
          </Menu.Item>
          <Menu.Item key="sa-my-performance" onClick={() => { onClose(); navigate("/my-performance"); }}>
            My Dashboard
          </Menu.Item>
        </>
      )}
      {isParent && (
        <Menu.Item key="sa-parent-dashboard" onClick={() => { onClose(); navigate("/parent-dashboard"); }}>
          Parent Dashboard
        </Menu.Item>
      )}
    </Menu>
  );

  const isStudentAssessmentActive = ["/question-bank", "/students", "/sections", "/my-tests", "/test", "/my-performance", "/parent-dashboard", "/cycles", "/blueprints", "/test-papers", "/performance", "/test-result", "/evaluate-responses", "/management-dashboard"].some(
    (p) => location.pathname.startsWith(p)
  );

  const navLinkStyle = "th-nav-link py-1 px-2 px-md-3 mb-3 mb-md-0";

  const menuItems = (
    <>
      <SchoolDropdown />

      {/* My Dashboard — staff only (not students/parents) */}
      {isStaff && !["coordinator", "admin", "superadmin"]?.includes(userType) && (
        <Link to="/dashboard" onClick={onClose}>
          <div className={`${navLinkStyle} ${isActive("/dashboard")}`}>
            My Dashboard
          </div>
        </Link>
      )}
      {["teacher", "principal", "coordinator"].includes(userType) && (
        <Link to="/shikshak-skill-list" onClick={onClose}>
          <div className={`${navLinkStyle} ${isActive("/shikshak-skill-list")}`}>
            Shikshak Skill
          </div>
        </Link>
      )}
      {userType === "teacher" && (
        <Link to="/my-courses" onClick={onClose}>
          <div className={`${navLinkStyle} ${isActive("/my-courses")}`}>
            My Courses
          </div>
        </Link>
      )}

      {/* Student Assessment dropdown */}
      <Dropdown overlay={studentAssessmentMenu} trigger={["click"]}>
        <div
          className={`${navLinkStyle} th-pointer ${
            isStudentAssessmentActive ? "th-active-menu" : ""
          }`}
        >
          <Space>
            {isStudent ? "My Assessment" : isParent ? "Child Performance" : "Student Assessment"}
            <DownOutlined style={{ fontSize: 10 }} />
          </Space>
        </div>
      </Dropdown>

      {/* Shikshak Utkarsh — staff only */}
      {isStaff && (
        <Link to="/utkarsh" onClick={onClose}>
          <div className={`${navLinkStyle} ${location.pathname.startsWith("/utkarsh") ? "th-active-menu" : ""}`}>
            Shikshak Utkarsh
          </div>
        </Link>
      )}

      {/* Self-observation (voice): teachers record their own classes; leaders see submitted ones */}
      {userType === "teacher" && (
        <Link to="/self-observation" onClick={onClose}>
          <div className={`${navLinkStyle} ${location.pathname.startsWith("/self-observation") ? "th-active-menu" : ""}`}>
            My Classes
          </div>
        </Link>
      )}
      {["principal", "academic_director", "coordinator", "hod", "admin", "superadmin"].includes(userType) && (
        <Link to="/self-observation/school" onClick={onClose}>
          <div className={`${navLinkStyle} ${location.pathname.startsWith("/self-observation") ? "th-active-menu" : ""}`}>
            Self-Observation
          </div>
        </Link>
      )}

      {/* Manage — admin/coordinator/principal/hod only */}
      {["hod", "principal", "coordinator", "admin", "superadmin", "academic_director"]?.includes(userType) && (
        <Link to="/manage" onClick={onClose}>
          <div className={`${navLinkStyle} ${isActive("/manage")}`}>
            Manage
          </div>
        </Link>
      )}
      <div
        className={`${navLinkStyle} th-pointer`}
        onClick={() => {
          onClose();
          handleLogout();
        }}
        style={{ opacity: 0.8 }}
      >
        Logout
      </div>
    </>
  );

  // Hide header/menu during test-taking for a distraction-free experience
  const isTestPage = location.pathname.startsWith("/test/");

  if (isTestPage) {
    return <>{children}</>;
  }

  return (
    <>
      <div>
        <div className="th-page-header container-fluid px-4 justify-content-between align-items-center">
          <div className="col-md-2">
            <Link to="/dashboard">
              <img height={46} src={Logo} className="th-pointer" alt="Logo" style={{ borderRadius: 6 }} />
            </Link>
          </div>
          <div className="col-md-10">
            <div className="d-none d-md-flex justify-content-end align-items-center">
              {menuItems}
            </div>
            <div className="d-md-none">
              <Button
                type="text"
                onClick={showDrawer}
                style={{ color: "#fff", fontSize: 20 }}
              >
                <MenuOutlined />
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Drawer
        title="Menu"
        placement="right"
        onClose={onClose}
        visible={visible}
        className="d-md-none"
      >
        <div className="d-flex flex-column">{menuItems}</div>
      </Drawer>
      <main className="th-bg-white">
        <div className="th-scrollbar-hidden" style={{ width: "100%" }}>
          {children}
        </div>
      </main>
    </>
  );
};

export default ClientLayout;
